// ==========================================================================
// lib/journal.ts — Isadora's journal (FF-053, 10 Oct 2026)
//   James: "design it extra special for caitlin to enjoy using". Moments with
//   photos, a book of firsts, growth, letters to her; grouped by her age.
//   Photos are stored by the Worker (R2), never read by AI.
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { differenceInCalendarDays, differenceInMonths, addMonths, parseISO } from "date-fns";
import { supabase } from "./supabase";
import { useHouseholdId, useMe } from "./household";
import { shrink, worker } from "./receipts";

export type EntryKind = "moment" | "first" | "growth" | "letter";
export type Entry = {
  id: string; person_id: string; happened_on: string; kind: EntryKind; first_key: string | null; title: string | null; body: string | null;
  mood: string | null; weight_g: number | null; length_mm: number | null; head_mm: number | null; hearts: string[]; created_by: string | null; created_at: string;
};
export type Photo = { id: string; entry_id: string; uploaded: boolean; caption: string | null; sort: number };

/** The book of firsts, in roughly the order they happen. "around" is a gentle guide, not a target. */
export const FIRSTS: { key: string; label: string; emoji: string; around: string }[] = [
  { key: "first_cuddle", label: "First cuddle", emoji: "🤱", around: "Day one" },
  { key: "first_bath", label: "First bath", emoji: "🛁", around: "First week" },
  { key: "first_outing", label: "First big outing", emoji: "🚗", around: "First weeks" },
  { key: "first_smile", label: "First real smile", emoji: "😊", around: "About 6 weeks" },
  { key: "first_laugh", label: "First laugh", emoji: "😂", around: "About 3 to 4 months" },
  { key: "sleeps_through", label: "Slept through the night", emoji: "🌙", around: "Whenever she's ready" },
  { key: "rolls_over", label: "Rolled over", emoji: "🔄", around: "About 4 to 6 months" },
  { key: "first_food", label: "First food", emoji: "🥑", around: "About 6 months" },
  { key: "sits_up", label: "Sat up on her own", emoji: "🧸", around: "About 6 to 8 months" },
  { key: "first_tooth", label: "First tooth", emoji: "🦷", around: "About 6 to 10 months" },
  { key: "first_swim", label: "First swim", emoji: "🏊‍♀️", around: "Any time" },
  { key: "first_beach", label: "First beach day", emoji: "🏖️", around: "Any time" },
  { key: "first_christmas", label: "First Christmas", emoji: "🎄", around: "December" },
  { key: "crawls", label: "Crawling", emoji: "🐢", around: "About 7 to 10 months" },
  { key: "waves", label: "First wave", emoji: "👋", around: "About 9 months" },
  { key: "first_word", label: "First word", emoji: "💬", around: "About 12 months" },
  { key: "stands", label: "Stood up", emoji: "🧍‍♀️", around: "About 10 to 12 months" },
  { key: "first_steps", label: "First steps", emoji: "👣", around: "About 12 months" },
  { key: "first_birthday", label: "First birthday", emoji: "🎂", around: "One year" },
  { key: "first_haircut", label: "First haircut", emoji: "✂️", around: "Whenever it's time" },
];
export const firstOf = (k: string | null) => FIRSTS.find((f) => f.key === k);

export const MOODS: { key: string; emoji: string; label: string }[] = [
  { key: "happy", emoji: "😊", label: "Happy" }, { key: "giggly", emoji: "😂", label: "Giggly" }, { key: "cuddly", emoji: "🥰", label: "Cuddly" },
  { key: "curious", emoji: "🧐", label: "Curious" }, { key: "sleepy", emoji: "😴", label: "Sleepy" }, { key: "grizzly", emoji: "😩", label: "Grizzly" },
  { key: "proud", emoji: "🌟", label: "Proud" },
];
export const moodOf = (k: string | null) => MOODS.find((m) => m.key === k);

/** "Day 3", "5 weeks", "3 months", "1 year 2 months". */
export function ageOn(born: string | null | undefined, on: string): string | null {
  if (!born) return null;
  const b = parseISO(born); const d = parseISO(on);
  const days = differenceInCalendarDays(d, b);
  if (days < 0) return null;
  if (days === 0) return "The day she was born";
  if (days < 14) return `Day ${days + 1}`;
  if (days < 8 * 7) return `${Math.floor(days / 7)} weeks`;
  const m = differenceInMonths(d, b);
  if (m < 12) { const w = Math.floor(differenceInCalendarDays(d, addMonths(b, m)) / 7); return `${m} month${m === 1 ? "" : "s"}${w ? ` ${w} week${w === 1 ? "" : "s"}` : ""}`; }
  const y = Math.floor(m / 12); const r = m % 12;
  return `${y} year${y === 1 ? "" : "s"}${r ? ` ${r} month${r === 1 ? "" : "s"}` : ""}`;
}
/** Which month of her life a day is in (0 = her first month). */
export const monthOf = (born: string, on: string) => Math.max(0, differenceInMonths(parseISO(on), parseISO(born)));

function useInv() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["journal", hid] });
}

export function useJournal() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["journal", hid], enabled: !!hid,
    queryFn: async () => {
      const [e, p] = await Promise.all([
        supabase.from("journal_entries").select("id,person_id,happened_on,kind,first_key,title,body,mood,weight_g,length_mm,head_mm,hearts,created_by,created_at").eq("household_id", hid).is("archived_at", null).order("happened_on", { ascending: false }).order("created_at", { ascending: false }),
        supabase.from("journal_photos").select("id,entry_id,uploaded,caption,sort").eq("household_id", hid).is("archived_at", null).order("sort"),
      ]);
      if (e.error) throw e.error; if (p.error) throw p.error;
      return { entries: e.data as Entry[], photos: p.data as Photo[] };
    },
  });
}

export type EntryDraft = Omit<Entry, "id" | "hearts" | "created_by" | "created_at"> & { id?: string };
/** Save an entry, then upload any new photos onto it. Returns the entry id. */
export function useSaveEntry() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useInv();
  return useMutation({
    mutationFn: async ({ d, files }: { d: EntryDraft; files: File[] }): Promise<string> => {
      const { id, ...row } = d;
      const clean = { ...row, title: row.title?.trim() || null, body: row.body?.trim() || null, first_key: row.kind === "first" ? row.first_key : null };
      let entryId = id;
      if (id) {
        const { error } = await supabase.from("journal_entries").update(clean).eq("id", id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from("journal_entries").insert({ ...clean, household_id: hid, created_by: me?.id ?? null }).select("id").single();
        if (error) throw new Error(error.code === "23505" ? "That first is already in her book." : error.message);
        entryId = (data as { id: string }).id;
      }
      let n = 0;
      for (const f of files) {
        const pid = crypto.randomUUID();
        const body = await shrink(f);
        const type = body.type || f.type || "image/jpeg";
        const { error } = await supabase.from("journal_photos").insert({ id: pid, household_id: hid, entry_id: entryId, file_key: `${hid}/journal/${pid}`, file_name: f.name, sort: n++ });
        if (error) throw error;
        const r = await worker(`/journal/${pid}/file`, { method: "PUT", headers: { "Content-Type": type }, body });
        if (!r.ok) {
          await supabase.from("journal_photos").update({ archived_at: new Date().toISOString() }).eq("id", pid);
          const j = (await r.json().catch(() => ({}))) as { message?: string };
          throw new Error(j.message ?? "A photo didn't upload. The words are saved; try the photo again.");
        }
      }
      return entryId!;
    },
    onSettled: inv,
  });
}

/** The photo's bytes, for saving to the phone or computer. */
export async function fetchPhoto(id: string): Promise<Blob> {
  const r = await worker(`/journal/${id}/file`);
  if (!r.ok) throw new Error("That photo couldn't be fetched.");
  return r.blob();
}
export const photoFileName = (kid: string, happened: string, n: number, type: string) => `${kid.toLowerCase()}-${happened}-${n}.${type.includes("png") ? "png" : type.includes("webp") ? "webp" : "jpg"}`;

export function useHeart() {
  const me = useMe(); const inv = useInv();
  return useMutation({
    mutationFn: async (e: Entry) => {
      if (!me) return;
      const hearts = e.hearts.includes(me.id) ? e.hearts.filter((x) => x !== me.id) : [...e.hearts, me.id];
      const { error } = await supabase.from("journal_entries").update({ hearts }).eq("id", e.id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

export function useArchiveEntry() {
  const inv = useInv();
  return useMutation({
    mutationFn: async (a: { entryId?: string; photoId?: string }) => {
      const at = new Date().toISOString();
      if (a.entryId) { const { error } = await supabase.from("journal_entries").update({ archived_at: at }).eq("id", a.entryId); if (error) throw error; }
      if (a.photoId) { const { error } = await supabase.from("journal_photos").update({ archived_at: at }).eq("id", a.photoId); if (error) throw error; }
    },
    onSuccess: inv,
  });
}

export function useSetBornOn() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { personId: string; born_on: string }) => {
      const { error } = await supabase.from("people").update({ born_on: a.born_on }).eq("id", a.personId);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["people", hid] }),
  });
}

/** Photos picked from the phone's Capture sheet, handed to the journal screen. */
let pending: File[] = [];
export const setPendingPhotos = (f: File[]) => { pending = f; };
export const takePendingPhotos = () => { const f = pending; pending = []; return f; };
